/**
 * Vocabulary Pronunciation Service.
 *
 * Historically a thin client around Azure Speech Pronunciation
 * Assessment. As of the v2 cutover it forwards to /speaking/assess
 * (Groq + Deepgram + WebAudio + Gemini). Single endpoint, single
 * pipeline — no fallback path needed at this layer because the
 * server's own AsrWithFallbackService handles ASR-level failover.
 *
 * Surface kept as `checkPronunciation(blob, word)` so the existing
 * FlashcardPracticePage call sites don't need a coordinated rewrite;
 * coach + prosody fields are surfaced on the result for sites that
 * want the new panels.
 */

import {
    speakingAssessmentService,
    SpeakingAssessmentResponse,
    CoachResult,
    ProsodySummary,
} from './speakingAssessmentService';

export interface PronunciationCheckResult {
  isCorrect: boolean;
  transcribedText: string;
  expectedWord: string;
  normalizedTranscribed: string;
  normalizedExpected: string;
  feedback?: string;
  accuracyScore?: number;
  overallScore?: number;
  fluencyScore?: number;
  completenessScore?: number;
  prosodyScore?: number;
  pronunciationScore?: number;
  phonemes?: { phoneme: string; accuracyScore: number }[];
  words?: {
    word: string;
    accuracyScore: number;
    errorType: string;
    phonemes?: { phoneme: string; accuracyScore: number }[];
    suggestion?: string;
  }[];
  /** v2 extras — surfaced for the FlashcardPracticePage panels.
   *  Undefined on legacy responses (none in production after v2). */
  coach?: CoachResult;
  prosody?: ProsodySummary;
  asrProvider?: string;
}

export interface PronunciationCheckResponse {
  success: boolean;
  data?: PronunciationCheckResult;
  error?: string;
}

/**
 * Convert audio blob to base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (e.g., "data:audio/webm;base64,")
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Get user's locale from browser or localStorage
 */
function getUserLocale(): string {
  // Check localStorage for user preference
  const savedLocale = localStorage.getItem('userLocale');
  if (savedLocale) return savedLocale;

  // Fallback to browser language
  const browserLang = navigator.language || 'zh-TW';

  // Map common browser languages to our supported locales
  if (browserLang.startsWith('zh-TW') || browserLang.startsWith('zh-Hant')) return 'zh-TW';
  if (browserLang.startsWith('zh')) return 'zh-CN';
  if (browserLang.startsWith('ja')) return 'ja';
  if (browserLang.startsWith('ko')) return 'ko';
  if (browserLang.startsWith('en')) return 'en';

  return 'zh-TW'; // Default
}

/**
 * Threshold for `isCorrect`. Picked to roughly match the Azure
 * default behaviour learners experienced before the cutover so the
 * "correct/wrong" judgment doesn't shift visibly. attemptNumber 1
 * (first try) uses a stricter cutoff to discourage immediately
 * mashing the mic.
 */
// Match the legacy Azure-backed thresholds so the v2 cutover doesn't
// shift learners' "correct/wrong" judgment. See
// `server/test/vocabulary/vocabulary-pronunciation.service.test.ts`
// for the contract these numbers anchor.
const FIRST_TRY_THRESHOLD = 80;
const RETRY_THRESHOLD = 60;

const normalize = (s: string): string =>
    s.toLowerCase().replace(/[^a-z0-9']/gi, '').trim();

/**
 * Adapt the v2 SpeakingAssessmentResponse to the legacy
 * PronunciationCheckResult shape. Single-word evaluations have
 * different "isCorrect" semantics than sentence-level shadowing —
 * the threshold is applied here so call sites keep their boolean
 * branches.
 */
function adaptToCheckResult(
    res: SpeakingAssessmentResponse,
    expectedWord: string,
    attemptNumber: number | undefined,
): PronunciationCheckResult {
    const transcribed = res.transcript || '';
    const normalizedTranscribed = normalize(transcribed);
    const normalizedExpected = normalize(expectedWord);
    const exactMatch = normalizedTranscribed === normalizedExpected;
    const threshold = (attemptNumber ?? 1) <= 1 ? FIRST_TRY_THRESHOLD : RETRY_THRESHOLD;
    const isCorrect = exactMatch && res.scores.accuracy >= threshold;

    return {
        isCorrect,
        transcribedText: transcribed,
        expectedWord,
        normalizedTranscribed,
        normalizedExpected,
        feedback: res.coach?.encouragement ?? '',
        accuracyScore: res.scores.accuracy,
        overallScore: res.scores.overall,
        fluencyScore: res.scores.fluency,
        completenessScore: res.scores.completeness,
        prosodyScore: res.scores.prosody,
        // v2 has no separate "pronunciationScore" — reuse accuracy.
        pronunciationScore: res.scores.accuracy,
        words: res.words.map(w => ({
            word: w.word,
            accuracyScore: w.accuracyScore,
            errorType: w.errorType,
            suggestion: w.suggestion,
        })),
        coach: res.coach,
        prosody: res.prosody,
        asrProvider: res.asrProvider,
    };
}

/**
 * Check pronunciation against an expected word or sentence.
 *
 * Mirrors the progressive sequence in pronunciationService:
 *   1. /speaking/assess — fast, returns scores + transcript.
 *   2. (optional) /speaking/coach — fired in the background if
 *      `onCoachReady` is provided. The host page slots the coach
 *      result into its state when it arrives.
 *
 * @param audioBlob       Recorded audio from MediaRecorder
 * @param expectedWord    Reference text (single word OR full sentence)
 * @param attemptNumber   1-indexed retry counter (drives strictness)
 * @param locale          BCP-47 locale for coach output
 * @param onCoachReady    Optional callback fired when the background
 *                        coach call resolves.
 */
export async function checkPronunciation(
  audioBlob: Blob,
  expectedWord: string,
  attemptNumber?: number,
  locale?: string,
  onCoachReady?: (coach: NonNullable<SpeakingAssessmentResponse['coach']>) => void,
): Promise<PronunciationCheckResponse> {
  try {
    const audioBase64 = await blobToBase64(audioBlob);
    const mimeType = audioBlob.type || 'audio/webm';
    const userLocale = locale || getUserLocale();

    const res = await speakingAssessmentService.assess({
      audioBase64,
      mimeType,
      referenceText: expectedWord,
      locale: userLocale,
    });

    if (onCoachReady) {
      speakingAssessmentService.generateCoach({
        referenceText: expectedWord,
        transcript: res.transcript,
        locale: userLocale,
        overallScore: res.scores.overall,
        prosody: {
          score: res.prosody.score,
          pitchRangeSt: res.prosody.pitchRangeSt,
          pitchVarianceSt: res.prosody.pitchVarianceSt,
          voicedRatio: res.prosody.voicedRatio,
        },
        issues: res.issues,
        words: res.words.map(w => ({ word: w.word, errorType: w.errorType })),
      })
        .then(onCoachReady)
        .catch(err => console.warn('[vocab pronunciation] coach fetch failed', err));
    }

    return {
      success: true,
      data: adaptToCheckResult(res, expectedWord, attemptNumber),
    };
  } catch (error: any) {
    console.error('Pronunciation check error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to check pronunciation',
    };
  }
}

const vocabularyPronunciationService = {
  checkPronunciation,
};

export default vocabularyPronunciationService;
